import { useOne, useGo, useUpdate, useCustom, useApiUrl } from "@refinedev/core";
import { useParams } from "react-router-dom";
import {
  MessageSquare,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  UserCheck,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormHeader } from "@/components/forms";
import { ChatPanel } from "@/components/chat";
import { JoinSupportCallButton } from "@/components/support-chat/JoinSupportCallButton";
import { EntityAnalyticsCard, EntityAnalyticsGrid } from "@/components/shared";
import type {
  SupportTicket,
  TicketStatus,
  TicketStatusCode,
  TicketStatusTransition,
} from "./types";

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Открыт",
  in_progress: "В работе",
  pending_user: "Ждёт пользователя",
  resolved: "Решён",
  closed: "Закрыт",
};

function getStatusBadge(status: TicketStatus) {
  switch (status) {
    case "open":
      return (
        <Badge className="bg-red-500/10 text-red-500">
          <AlertCircle className="h-3 w-3 mr-1" />
          Открыт
        </Badge>
      );
    case "in_progress":
      return (
        <Badge className="bg-yellow-500/10 text-yellow-500">
          <Clock className="h-3 w-3 mr-1" />В работе
        </Badge>
      );
    case "pending_user":
      return (
        <Badge className="bg-blue-500/10 text-blue-500">
          <Clock className="h-3 w-3 mr-1" />Ждёт пользователя
        </Badge>
      );
    case "resolved":
      return (
        <Badge className="bg-green-500/10 text-green-500">
          <CheckCircle className="h-3 w-3 mr-1" />
          Решён
        </Badge>
      );
    case "closed":
      return <Badge variant="secondary">Закрыт</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Support ticket show/detail page
 */
export function SupportTicketsShow() {
  const go = useGo();
  const apiUrl = useApiUrl();
  const { id } = useParams<{ id: string }>();

  const ticketResult = useOne<SupportTicket>({
    resource: "support-tickets",
    id: Number(id),
  });

  const ticket = ticketResult.result;
  const isLoading = ticketResult.query?.isLoading ?? false;

  const transitionsResult = useCustom<TicketStatusTransition[]>({
    url: `${apiUrl}/support-tickets/${id}/allowed-transitions`,
    method: "get",
    queryOptions: { enabled: Boolean(ticket) },
  });
  const allowedTransitions = transitionsResult.query?.data?.data ?? [];

  const { mutate: updateTicket, mutation: updateMutation } = useUpdate<SupportTicket>();
  const isUpdating = updateMutation.isPending;

  const handleStatusChange = (newStatus: TicketStatusCode) => {
    if (!ticket) {
      return;
    }
    updateTicket(
      {
        resource: "support-tickets",
        id: ticket.id,
        values: { status: newStatus },
      },
      {
        onSuccess: () => {
          ticketResult.query?.refetch();
          transitionsResult.query?.refetch();
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Обращение не найдено</p>
        <Button variant="link" onClick={() => go({ to: "/support-tickets" })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const currentStatusUpper = ticket.status.toUpperCase() as TicketStatusCode;

  return (
    <div className="space-y-6">
      <FormHeader
        title={`Обращение #${ticket.id}`}
        description={ticket.question}
        onBack={() => go({ to: "/support-tickets" })}
        titleAddon={getStatusBadge(ticket.status)}
        icon={<MessageSquare className="h-5 w-5" />}
        sticky
      />

      {/* Analytics cards */}
      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Статус"
          value={STATUS_LABELS[ticket.status]}
          icon={<AlertCircle className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Создан"
          value={formatDate(ticket.createdAt)}
          icon={<Calendar className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Решён"
          value={ticket.resolvedAt ? formatDate(ticket.resolvedAt) : "—"}
          icon={<CheckCircle className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Назначен"
          value={ticket.assignedAdmin?.email ?? "Нет"}
          icon={<UserCheck className="h-4 w-4" />}
        />
      </EntityAnalyticsGrid>

      {/* Question card */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            Вопрос
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {ticket.question}
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Chat Area */}
        <Card className="md:col-span-2 flex flex-col h-[700px] min-h-0 overflow-hidden py-0">
          <CardHeader className="shrink-0 py-4 border-b flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">Переписка</CardTitle>
            <JoinSupportCallButton chatRoomId={ticket.chatRoomId} />
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-hidden p-0">
            <ChatPanel
              chatRoomId={ticket.chatRoomId}
              userId={ticket.userId}
              height="100%"
              showHeader={false}
              className="h-full border-0 rounded-none"
            />
          </CardContent>
        </Card>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Пользователь</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <User className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <div className="font-medium">
                    {[ticket.user?.firstName, ticket.user?.lastName]
                      .filter(Boolean)
                      .join(" ") || "Пользователь"}
                  </div>
                  {ticket.user?.username && (
                    <div className="text-sm text-muted-foreground">
                      @{ticket.user.username}
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => go({ to: `/users/show/${ticket.userId}` })}
              >
                Открыть профиль
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Статус обращения</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Select
                value={currentStatusUpper}
                onValueChange={(v) => {
                  if (v !== currentStatusUpper) {
                    handleStatusChange(v as TicketStatusCode);
                  }
                }}
                disabled={isUpdating}
              >
                <SelectTrigger className="w-full">
                  {isUpdating ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Обновление…
                    </span>
                  ) : (
                    <SelectValue />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={currentStatusUpper} disabled>
                    {STATUS_LABELS[ticket.status]} (текущий)
                  </SelectItem>
                  {allowedTransitions.map((t) => (
                    <SelectItem key={t.status} value={t.status}>
                      {t.label}
                    </SelectItem>
                  ))}
                  {allowedTransitions.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground italic">
                      Переходы недоступны
                    </div>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Чат остаётся открытым, пока статус не «Решён» или «Закрыт».
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
